import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconArrowLeft, IconHomeHeart, IconSchool, IconFriends, IconStethoscope,
  IconBuildingCommunity,
} from "@tabler/icons-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/site/SiteNav";

const ROLE_ICONS = {
  familia: <IconHomeHeart size={22} />,
  docente: <IconSchool size={22} />,
  sombra: <IconFriends size={22} />,
  psicologa: <IconStethoscope size={22} />,
  escuela: <IconBuildingCommunity size={22} />,
};

export default function Login() {
  const { t, lang, toggle } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      let next = searchParams.get("next");
      if (!next) { try { next = sessionStorage.getItem("chiessa.postLogin"); } catch { /* noop */ } }
      try { sessionStorage.removeItem("chiessa.postLogin"); } catch { /* noop */ }
      nav(next && next.startsWith("/") ? next : "/app");
    }
    else setError(r.error);
  };

  return (
    <AuthShell>
      <p className="eyebrow">{t("auth.login_title")}</p>
      <h1 className="font-display font-medium text-[34px] mt-2 leading-tight">
        {lang === "es" ? "¿Quién eres hoy?" : "Who are you today?"}
      </h1>
      <p className="text-muted text-[14px] mt-3 max-w-[38em]">{t("auth.login_lead")}</p>

      <form className="mt-8 space-y-4 max-w-[420px]" onSubmit={submit}>
        <div className="field">
          <label>{t("auth.email")}</label>
          <input
            className="input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            data-testid="login-email"
          />
        </div>
        <div className="field">
          <label>{t("auth.password")}</label>
          <input
            className="input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="login-password"
          />
        </div>
        {error && <p className="text-errtext text-[13px]" data-testid="login-error">{error}</p>}
        <button type="submit" className="btn w-full justify-center" disabled={loading} data-testid="login-submit">
          {loading ? "..." : t("auth.submit_login")}
        </button>
      </form>

      <div className="mt-4 text-[12.5px]">
        <Link
          to="/forgot-password"
          className="text-tert hover:text-teal-deep transition"
          data-testid="login-forgot-password-link"
        >
          {lang === "es" ? "¿Olvidaste tu contraseña?" : "Forgot your password?"}
        </Link>
      </div>

      <div className="mt-6 text-[13.5px]">
        {t("auth.no_account")} {" "}
        <Link to="/register" className="text-teal font-medium underline underline-offset-4" data-testid="link-register">
          {t("auth.register_here")}
        </Link>
      </div>

      <div className="mt-10">
        <p className="eyebrow mb-4">{lang === "es" ? "Roles" : "Roles"}</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {["familia", "docente", "sombra", "psicologa", "escuela"].map((r) => (
            <div key={r} className="card text-center py-3 opacity-80">
              <div className="w-9 h-9 mx-auto rounded-lg bg-teal-soft text-teal-deep flex items-center justify-center">
                {ROLE_ICONS[r]}
              </div>
              <p className="text-[12px] mt-2 font-medium">{t(`roles.${r}`)}</p>
            </div>
          ))}
        </div>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  const { t, toggle, lang } = useI18n();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <div className="glass-bar sticky top-0 z-40 h-14">
        <div className="wrap h-full flex items-center justify-between">
          <button className="flex items-center gap-2.5" onClick={() => nav("/")} data-testid="auth-brand">
            <BrandLogo />
            <span className="font-display text-[17px] tracking-tight">Chiessa<span className="text-honey">.</span> Institute</span>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="btn-ghost btn text-[12px] px-2.5 py-1.5" data-testid="auth-lang-toggle">
              <span className="font-mono uppercase tracking-widest text-[11px]">{lang === "es" ? "ES / EN" : "EN / ES"}</span>
            </button>
            <button className="btn-ghost btn text-[12px] px-3 py-1.5" onClick={() => nav("/")} data-testid="auth-back">
              <IconArrowLeft size={13} /> {t("auth.back")}
            </button>
          </div>
        </div>
      </div>
      <main className="flex-1 py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="wrap"
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
